import React, { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  ArrowDownLeft,
  ArrowUpRight,
  ArrowRight,
  TrendingUp,
  TrendingDown,
  Wallet,
  Calendar,
  Download,
  Building2,
  AlertTriangle,
  Scale,
  ExternalLink,
} from "lucide-react";
import { useLocation, Link } from "wouter";
import { CATEGORY_LABELS, formatAmount } from "@shared/types";
import type { TransactionCategory } from "@shared/types";
import { useLanguage, CATEGORY_LABELS_AR } from "@/contexts/LanguageContext";
import { useAuth } from "@/_core/hooks/useAuth";
import { toast } from "sonner";
import { RouteFeedback } from "@/components/RouteFeedback";

function getMonthRange(year: number, month: number) {
  const start = new Date(year, month, 1).getTime();
  const end = new Date(year, month + 1, 0, 23, 59, 59, 999).getTime();
  return { start, end };
}

// ─── Number formatting for PDF ────────────────────────────────────────────────
function fmtNum(n: number): string {
  if (n === 0) return "—";
  const abs = Math.abs(n);
  const formatted = abs.toLocaleString("en-US", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
  return n < 0 ? `(${formatted})` : formatted;
}

function formatPeriodEnd(endDate: number, language: string) {
  const d = new Date(endDate);
  return d.toLocaleDateString(language === "ar" ? "ar-KW" : "en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// Extended labels for categories stored in DB
const EXTRA_LABELS_EN: Record<string, string> = {
  bills_utilities: "Bills & Utilities",
  fees_charges: "Fees & Charges",
  atm_withdrawal: "ATM Withdrawal",
  credit_card_payment: "Credit Card Payment",
  transfer: "Transfer",
  other: "Other",
};
const EXTRA_LABELS_AR: Record<string, string> = {
  bills_utilities: "فواتير وخدمات",
  fees_charges: "رسوم ومصاريف",
  atm_withdrawal: "سحب صراف آلي",
  credit_card_payment: "دفع بطاقة ائتمان",
  transfer: "تحويل",
  other: "أخرى",
};

/** Format account label for display in dropdown */
function formatAccountLabel(accountNumber: string, bankSource?: string | null, accountLabel?: string | null): string {
  if (accountLabel) return accountLabel;
  if (accountNumber.endsWith('-Main')) {
    const bank = accountNumber.replace('-Main', '');
    return `${bank} Account`;
  }
  if (accountNumber.startsWith('xx')) {
    const digits = accountNumber.replace('xx', '');
    const bank = bankSource?.includes('KFH') ? 'KFH' : (bankSource || '');
    return `${bank} ••${digits}`;
  }
  if (accountNumber === 'KFH-other') {
    return 'KFH Other';
  }
  if (/^\d+$/.test(accountNumber)) {
    const bank = bankSource || '';
    const shortBank = bank.includes('NBK') || bank.includes('National Bank') ? 'NBK'
      : bank.includes('Boubyan') ? 'Boubyan'
      : bank.includes('KFH') ? 'KFH'
      : bank;
    return `${shortBank} ${accountNumber}`;
  }
  return accountNumber;
}

export default function IncomeStatement() {
  const [, setLocation] = useLocation();
  const { t, language, isRTL } = useLanguage();
  const { user } = useAuth();
  const getCategoryLabel = (cat: string) => {
    const c = cat === "bills_utilities" ? "bills" : cat;
    if (language === "ar") {
      return EXTRA_LABELS_AR[c] ?? CATEGORY_LABELS_AR[c] ?? CATEGORY_LABELS[c as TransactionCategory] ?? c.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
    }
    return EXTRA_LABELS_EN[c] ?? CATEGORY_LABELS[c as TransactionCategory] ?? c.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  };
  const now = useMemo(() => new Date(), []);
  const [selectedMonth, setSelectedMonth] = useState(() => `${now.getFullYear()}-${now.getMonth()}`);
  const [selectedAccount, setSelectedAccount] = useState<string>("all");

  const [year, month] = selectedMonth.split("-").map(Number);
  const { start: startDate, end: endDate } = useMemo(() => getMonthRange(year, month), [year, month]);

  const accountsQuery = trpc.transaction.accounts.useQuery();
  const { data: accounts } = accountsQuery;
  const accountFilter = selectedAccount === "all" ? undefined : selectedAccount;

  const summaryQuery = trpc.transaction.summary.useQuery({ startDate, endDate, accountNumber: accountFilter });
  const incomeCatsQuery = trpc.transaction.categoryBreakdown.useQuery({ type: "cash_in", startDate, endDate, accountNumber: accountFilter });
  const expenseCatsQuery = trpc.transaction.categoryBreakdown.useQuery({ type: "cash_out", startDate, endDate, accountNumber: accountFilter });
  const balanceQuery = trpc.transaction.latestBalance.useQuery({ startDate, endDate, accountNumber: accountFilter });
  const cashFlowQuery = trpc.transaction.cashFlow.useQuery({ startDate, endDate, accountNumber: accountFilter });
  const { data: summary, isLoading: summaryLoading } = summaryQuery;
  const { data: incomeCats } = incomeCatsQuery;
  const { data: expenseCats } = expenseCatsQuery;
  const { data: balance } = balanceQuery;
  const { data: cashFlow } = cashFlowQuery;

  // Fetch adjustment transactions for this period to show disclosure
  const adjustmentTxnsQuery = trpc.transaction.list.useQuery({
    startDate,
    endDate,
    accountNumber: accountFilter,
    limit: 200,
  });
  const { data: adjustmentTxns } = adjustmentTxnsQuery;
  const pageQueries = [accountsQuery, summaryQuery, incomeCatsQuery, expenseCatsQuery, balanceQuery, cashFlowQuery, adjustmentTxnsQuery];
  const isPageLoading = pageQueries.some((query) => query.isLoading);
  const isPageError = pageQueries.some((query) => query.isError);
  const retryPageQueries = () => { pageQueries.forEach((query) => { void query.refetch(); }); };
  const adjustmentStats = useMemo(() => {
    if (!adjustmentTxns) return { count: 0, netTotal: 0 };
    const adjs = adjustmentTxns.filter((t) => t.isAdjustment === 1);
    const net = adjs.reduce((sum, t) => {
      const amt = parseFloat(t.amount);
      return sum + (t.type === "cash_in" ? amt : -amt);
    }, 0);
    return { count: adjs.length, netTotal: net };
  }, [adjustmentTxns]);

  const cashIn = summary?.find((s) => s.type === "cash_in");
  const cashOut = summary?.find((s) => s.type === "cash_out");
  const totalIn = parseFloat(cashIn?.total ?? "0");
  const totalOut = parseFloat(cashOut?.total ?? "0");
  const netFlow = totalIn - totalOut;
  const txCount = (parseInt(String(cashIn?.count ?? "0")) + parseInt(String(cashOut?.count ?? "0")));
  const openingBalance = cashFlow?.openingBalance ?? null;
  const closingBalance = cashFlow?.closingBalance ?? null;

  const monthOptions = useMemo(() => {
    const opts = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      opts.push({
        value: `${d.getFullYear()}-${d.getMonth()}`,
        label: d.toLocaleString(language === "ar" ? "ar-KW" : "default", { month: "long", year: "numeric" }),
      });
    }
    return opts;
  }, [now]);

  // Use cashFlow opening/closing balances for the selected month
  const startingBalance = cashFlow?.openingBalance ?? null;
  const currentBalance = cashFlow?.closingBalance ?? null;

  // Period label for PDF
  const periodLabel = useMemo(() => {
    const d = new Date(year, month);
    return d.toLocaleString(language === "ar" ? "ar-KW" : "en-US", { month: "long", year: "numeric" });
  }, [year, month, language]);

  // Cash flow summary paragraph for PDF
  const cashFlowParagraph = useMemo(() => {
    if (txCount === 0) return "";
    const topExpenses = expenseCats?.slice(0, 3).map((c) => getCategoryLabel(c.category ?? "uncategorized")) ?? [];
    const topIncome = incomeCats?.slice(0, 2).map((c) => getCategoryLabel(c.category ?? "uncategorized")) ?? [];
    const expenseRatio = totalIn > 0 ? ((totalOut / totalIn) * 100).toFixed(0) : "N/A";
    const savingsRate = totalIn > 0 ? (((totalIn - totalOut) / totalIn) * 100).toFixed(0) : "N/A";
    if (language === "ar") {
      const status = netFlow >= 0 ? "فائض" : "عجز";
      return `خلال فترة ${periodLabel}، بلغ إجمالي الإيرادات ${fmtNum(totalIn)} د.ك من ${txCount} معاملة. ` +
        (topIncome.length > 0 ? `تمثلت مصادر الدخل الرئيسية في ${topIncome.join(" و")}. ` : "") +
        `بلغ إجمالي المصروفات التشغيلية ${fmtNum(totalOut)} د.ك` +
        (topExpenses.length > 0 ? `، حيث شكلت ${topExpenses.join(" و")} أكبر فئات الإنفاق` : "") +
        `. نتج عن ذلك ${status} صافي قدره ${fmtNum(Math.abs(netFlow))} د.ك. ` +
        (totalIn > 0 ? `بلغت نسبة المصروفات إلى الإيرادات ${expenseRatio}% مع معدل ادخار ${savingsRate}%.` : "");
    }
    const status = netFlow >= 0 ? "a surplus" : "a deficit";
    return `During the ${periodLabel} period, total revenue amounted to KWD ${fmtNum(totalIn)} across ${txCount} transactions. ` +
      (topIncome.length > 0 ? `Primary income sources included ${topIncome.join(" and ")}. ` : "") +
      `Total operating expenses were KWD ${fmtNum(totalOut)}` +
      (topExpenses.length > 0 ? `, with ${topExpenses.join(", ")} representing the largest expense categories` : "") +
      `. This resulted in ${status} of KWD ${fmtNum(Math.abs(netFlow))}. ` +
      (totalIn > 0 ? `The expense-to-revenue ratio stood at ${expenseRatio}%, yielding a savings rate of ${savingsRate}%.` : "");
  }, [txCount, totalIn, totalOut, netFlow, periodLabel, incomeCats, expenseCats, language]);

  // ─── PDF Export ─────────────────────────────────────────────────────────────
  const exportPDF = () => {
    if (txCount === 0) {
      toast.error(language === "ar" ? "لا توجد بيانات للتصدير" : "No data to export");
      return;
    }
    try {
      const userName = user?.name ?? "Account Holder";
      const periodEndFormatted = formatPeriodEnd(endDate, language);

      const revenueRows = (incomeCats ?? []).map((c) => `
        <tr>
          <td class="line-item">${getCategoryLabel(c.category ?? "uncategorized")}</td>
          <td class="amount">${fmtNum(parseFloat(c.total ?? "0"))}</td>
        </tr>
      `).join("");

      const expenseRows = (expenseCats ?? []).map((c) => `
        <tr>
          <td class="line-item">${getCategoryLabel(c.category ?? "uncategorized")}</td>
          <td class="amount">${fmtNum(parseFloat(c.total ?? "0"))}</td>
        </tr>
      `).join("");

      const html = `<!DOCTYPE html>
<html dir="${isRTL ? "rtl" : "ltr"}">
<head>
  <meta charset="UTF-8">
  <title>Statement of Income - ${periodLabel}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Inter', -apple-system, sans-serif;
      color: #1a1a2e;
      background: #fff;
      padding: 48px 56px;
      font-size: 13px;
      line-height: 1.6;
    }
    .header {
      border-bottom: 3px solid #1a1a2e;
      padding-bottom: 20px;
      margin-bottom: 8px;
    }
    .header h1 { font-size: 15px; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase; margin-bottom: 2px; }
    .header .entity { font-size: 22px; font-weight: 700; color: #1a1a2e; margin-bottom: 4px; }
    .header .subtitle { font-size: 14px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.3px; color: #333; }
    .header .period { font-size: 12px; color: #666; margin-top: 2px; }
    .header .unaudited { font-size: 11px; color: #888; font-style: italic; }
    table { width: 100%; border-collapse: collapse; margin: 16px 0; }
    th { text-align: ${isRTL ? "right" : "left"}; font-size: 11px; font-weight: 600; color: #666; text-transform: uppercase; letter-spacing: 0.5px; padding: 8px 0; border-bottom: 1px solid #ddd; }
    th.amount-header { text-align: ${isRTL ? "left" : "right"}; width: 160px; }
    td { padding: 6px 0; vertical-align: top; }
    td.line-item { padding-${isRTL ? "right" : "left"}: 24px; color: #333; }
    td.amount { text-align: ${isRTL ? "left" : "right"}; font-variant-numeric: tabular-nums; font-weight: 500; width: 160px; }
    .section-header td { font-weight: 700; color: #1a1a2e; padding-top: 16px; padding-bottom: 4px; font-size: 13px; }
    .subtotal td { font-weight: 700; color: #1a1a2e; border-top: 1px solid #333; padding-top: 8px; padding-bottom: 8px; }
    .subtotal td.amount { font-weight: 700; }
    .net-total td { font-weight: 700; font-size: 14px; color: #1a1a2e; border-top: 2px solid #1a1a2e; border-bottom: 3px double #1a1a2e; padding-top: 10px; padding-bottom: 10px; }
    .separator td { padding: 4px 0; }
    .separator td hr { border: none; border-top: 1px solid #ddd; }
    .cash-flow-statement { page-break-before: always; margin-top: 0; padding-top: 24px; }
    .cash-flow-statement h2 { font-size: 14px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.3px; color: #333; margin-bottom: 4px; }
    .cash-flow-statement .cf-period { font-size: 12px; color: #666; margin-bottom: 16px; }
    .cash-flow-section { margin-top: 24px; padding: 20px 24px; background: #f8f9fa; border: 1px solid #e9ecef; border-radius: 4px; }
    .cash-flow-section h3 { font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; color: #1a1a2e; margin-bottom: 12px; }
    .cash-flow-section p { font-size: 12px; line-height: 1.8; color: #444; text-align: justify; }
    .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #ddd; font-size: 10px; color: #999; display: flex; justify-content: space-between; }
    .currency-note { text-align: ${isRTL ? "left" : "right"}; font-size: 11px; color: #888; margin-bottom: 8px; }
    .toolbar { position: sticky; top: 0; z-index: 100; background: #1a1a2e; color: #fff; display: flex; align-items: center; gap: 10px; padding: 10px 24px; margin: -48px -56px 24px -56px; font-family: 'Inter', -apple-system, sans-serif; }
    .toolbar button { background: rgba(255,255,255,0.15); color: #fff; border: 1px solid rgba(255,255,255,0.25); padding: 8px 20px; border-radius: 8px; font-size: 14px; font-weight: 500; cursor: pointer; font-family: inherit; }
    .toolbar button:active { background: rgba(255,255,255,0.3); }
    @media print { body { padding: 24px 32px; } .toolbar { display: none !important; } .cash-flow-statement { page-break-before: always; } .cash-flow-section { break-inside: avoid; } }
  </style>
</head>
<body>
  <div class="toolbar">
    <button onclick="window.close(); if(!window.closed) history.back();">\u2715 ${language === "ar" ? "إغلاق" : "Close"}</button>
    <button onclick="window.print();">\u2B07 ${language === "ar" ? "حفظ PDF" : "Save PDF"}</button>
  </div>
  <div class="header">
    <div class="entity">${userName}</div>
    <div class="subtitle">STATEMENT OF INCOME</div>
    <div class="period">${periodLabel} &mdash; Period Ended ${periodEndFormatted}</div>
    <div class="unaudited">(Personal &middot; Unaudited)</div>
  </div>
  <div class="currency-note">Amounts in KWD</div>
  <table>
    <thead><tr><th></th><th class="amount-header">${periodLabel.toUpperCase()}</th></tr></thead>
    <tbody>
      <tr class="section-header"><td>Cash In</td><td></td></tr>
      ${revenueRows || '<tr><td class="line-item" style="color:#999;font-style:italic;">No cash in recorded</td><td class="amount">—</td></tr>'}
      <tr class="separator"><td colspan="2"><hr/></td></tr>
      <tr class="subtotal"><td>Total Cash In</td><td class="amount">${fmtNum(totalIn)}</td></tr>
      <tr class="section-header"><td>Cash Out</td><td></td></tr>
      ${expenseRows || '<tr><td class="line-item" style="color:#999;font-style:italic;">No cash out recorded</td><td class="amount">—</td></tr>'}
      <tr class="separator"><td colspan="2"><hr/></td></tr>
      <tr class="subtotal"><td>Total Cash Out</td><td class="amount">${fmtNum(totalOut)}</td></tr>
      <tr class="section-header"><td colspan="2"></td></tr>
      <tr class="net-total"><td>Net Cash Flow</td><td class="amount">${fmtNum(netFlow)}</td></tr>
    </tbody>
  </table>
  <div class="cash-flow-statement">
    <h2>STATEMENT OF CASH FLOWS</h2>
    <div class="cf-period">${periodLabel} &mdash; Period Ended ${periodEndFormatted}</div>
    <table>
      <thead><tr><th></th><th class="amount-header">${periodLabel.toUpperCase()}</th></tr></thead>
      <tbody>
        <tr class="section-header"><td>Cash Balance</td><td></td></tr>
        <tr><td class="line-item">Opening Balance</td><td class="amount">${openingBalance !== null ? fmtNum(openingBalance) : "N/A"}</td></tr>
        <tr class="section-header"><td>Cash Movements During Period</td><td></td></tr>
        <tr><td class="line-item">Total Cash In</td><td class="amount" style="color:#16a34a;">${fmtNum(totalIn)}</td></tr>
        <tr><td class="line-item">Total Cash Out</td><td class="amount" style="color:#dc2626;">(${fmtNum(totalOut)})</td></tr>
        <tr class="separator"><td colspan="2"><hr/></td></tr>
        <tr class="subtotal"><td>Net Movement</td><td class="amount" style="color:${netFlow >= 0 ? '#16a34a' : '#dc2626'};">${netFlow >= 0 ? '+' : ''}${fmtNum(netFlow)}</td></tr>
        <tr class="section-header"><td colspan="2"></td></tr>
        <tr class="net-total"><td>Closing Balance</td><td class="amount">${closingBalance !== null ? fmtNum(closingBalance) : "N/A"}</td></tr>
      </tbody>
    </table>
  </div>
  ${cashFlowParagraph ? `<div class="cash-flow-section"><h3>Notes</h3><p>${cashFlowParagraph}</p></div>` : ""}
  <div class="footer">
    <span>Generated by Mizano</span>
    <span>${new Date().toLocaleDateString(language === "ar" ? "ar-KW" : "en-US", { day: "numeric", month: "long", year: "numeric" })}</span>
  </div>
</body>
</html>`;

      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        toast.error(language === "ar" ? "يرجى السماح بالنوافذ المنبثقة" : "Please allow popups to export PDF");
        return;
      }
      printWindow.document.write(html);
      printWindow.document.close();
      toast.success(language === "ar" ? "تم فتح التقرير - استخدم مشاركة أو حفظ كـ PDF" : "Report opened \u2014 use Share or Save as PDF");
    } catch {
      toast.error(language === "ar" ? "فشل التصدير" : "Failed to export");
    }
  };

  if (isPageLoading || isPageError) {
    return <div dir={isRTL ? "rtl" : "ltr"}><RouteFeedback isLoading={isPageLoading} isError={isPageError} onRetry={retryPageQueries} label={t("income.title")} /></div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6" dir={isRTL ? "rtl" : "ltr"}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-foreground">{t("income.title")}</h1>
          <p className="text-muted-foreground text-xs sm:text-sm mt-0.5 sm:mt-1">{t("income.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-[120px] sm:w-[180px] h-9 sm:h-10 text-xs sm:text-sm">
              <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 flex-shrink-0" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedAccount} onValueChange={setSelectedAccount}>
            <SelectTrigger className="w-[120px] sm:w-[170px] h-9 sm:h-10 text-xs sm:text-sm">
              <Building2 className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0 hidden sm:block" />
              <SelectValue placeholder={language === "ar" ? "جميع الحسابات" : "All Accounts"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{language === "ar" ? "جميع الحسابات" : "All Accounts"}</SelectItem>
              {accounts && accounts.filter((acc) => acc.accountNumber != null && acc.isActive !== 0).map((acc) => (
                <SelectItem key={acc.accountNumber!} value={acc.accountNumber!}>
                  {formatAccountLabel(acc.accountNumber!, acc.bankSource, acc.accountLabel)}
                  <span className="ml-1 text-muted-foreground">({acc.count})</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={exportPDF} disabled={txCount === 0} size="sm" className="gap-1.5 h-9 sm:h-10 px-3 sm:px-4">
            <Download className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            <span className="text-xs sm:text-sm">PDF</span>
          </Button>
        </div>
      </div>

          <div className="space-y-4 sm:space-y-6">
          {/* Balance Overview -- gradient like Budgets summary */}
          <div className="rounded-xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20 p-3 sm:p-5">
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div className="bg-background/60 backdrop-blur-sm rounded-lg p-3 sm:p-4 border border-border/50">
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="h-7 w-7 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Wallet className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] sm:text-xs text-muted-foreground">{language === "ar" ? "رصيد بداية الشهر" : "Opening Balance"}</p>
                    <p className="text-xs sm:text-xl font-bold text-foreground whitespace-nowrap leading-tight mt-0.5">
                      {startingBalance != null ? formatAmount(startingBalance) : "—"}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-background/60 backdrop-blur-sm rounded-lg p-3 sm:p-4 border border-border/50">
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="h-7 w-7 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Wallet className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] sm:text-xs text-muted-foreground">{language === "ar" ? "رصيد نهاية الشهر" : "Closing Balance"}</p>
                    <p className="text-xs sm:text-xl font-bold text-foreground whitespace-nowrap leading-tight mt-0.5">
                      {currentBalance != null ? formatAmount(currentBalance) : "—"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Revenue / Expenses / Net Income / Transactions -- clickable summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4">
            <div
              className="rounded-xl bg-card border-l-4 p-3 sm:p-4 cursor-pointer hover:shadow-md transition-shadow"
              style={{ borderLeftColor: "var(--income)" }}
              onClick={() => setLocation(`/transactions?type=cash_in&month=${selectedMonth}`)}
            >
              <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">{language === "ar" ? "الداخل" : "Cash In"}</p>
              <p className="text-xs sm:text-xl font-bold mt-1 whitespace-nowrap leading-tight" style={{ color: "var(--income)" }}>
                {totalIn > 0 ? formatAmount(totalIn) : "—"}
              </p>
              <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-0.5">
                {language === "ar" ? "عرض التفاصيل" : "View details"} <ArrowRight className="h-2.5 w-2.5" />
              </p>
            </div>
            <div
              className="rounded-xl bg-card border-l-4 p-3 sm:p-4 cursor-pointer hover:shadow-md transition-shadow"
              style={{ borderLeftColor: "var(--expense)" }}
              onClick={() => setLocation(`/transactions?type=cash_out&month=${selectedMonth}`)}
            >
              <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">{language === "ar" ? "الخارج" : "Cash Out"}</p>
              <p className="text-xs sm:text-xl font-bold mt-1 whitespace-nowrap leading-tight" style={{ color: "var(--expense)" }}>
                {totalOut > 0 ? formatAmount(totalOut) : "—"}
              </p>
              <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-0.5">
                {language === "ar" ? "عرض التفاصيل" : "View details"} <ArrowRight className="h-2.5 w-2.5" />
              </p>
            </div>
            <div
              className="rounded-xl border-l-4 p-3 sm:p-4 cursor-pointer hover:shadow-md transition-shadow"
              style={{
                borderLeftColor: netFlow >= 0 ? "oklch(0.55 0.18 155)" : "oklch(0.55 0.18 25)",
                backgroundColor: netFlow >= 0 ? "oklch(0.85 0.12 155 / 0.15)" : "oklch(0.75 0.12 25 / 0.15)",
              }}
              onClick={() => setLocation("/analytics")}
            >
              <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">{language === "ar" ? "صافي التدفق" : "Net Cash Flow"}</p>
              <p className="text-xs sm:text-xl font-bold mt-1 whitespace-nowrap leading-tight" style={{ color: netFlow >= 0 ? "var(--income)" : "var(--expense)" }}>
                {netFlow >= 0 ? "+" : ""}{formatAmount(netFlow)}
              </p>
              <p className="text-[10px] mt-1 flex items-center gap-0.5" style={{ color: netFlow >= 0 ? "var(--income)" : "var(--expense)" }}>
                {netFlow >= 0 ? (language === "ar" ? "فائض" : "Surplus") : (language === "ar" ? "عجز" : "Deficit")}
              </p>
            </div>
            <div
              className="rounded-xl bg-card border-l-4 border-l-primary p-3 sm:p-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setLocation(`/transactions?month=${selectedMonth}`)}
            >
              <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">{language === "ar" ? "المعاملات" : "Transactions"}</p>
              <p className="text-xs sm:text-xl font-bold mt-1 text-primary whitespace-nowrap leading-tight">
                {txCount || "—"}
              </p>
              <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-0.5">
                {language === "ar" ? "عرض الكل" : "View all"} <ArrowRight className="h-2.5 w-2.5" />
              </p>
            </div>
          </div>

          {/* Cash In Section -- green accent border */}
          <Card className="border-l-4" style={{ borderLeftColor: "var(--income)" }}>
            <CardHeader className="pb-2 sm:pb-3 px-3 sm:px-6 pt-3 sm:pt-6">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <div className="h-6 w-6 rounded-md flex items-center justify-center" style={{ backgroundColor: "oklch(0.65 0.18 155 / 0.12)" }}>
                    <ArrowDownLeft className="h-3.5 w-3.5" style={{ color: "var(--income)" }} />
                  </div>
                  <CardTitle className="text-sm sm:text-base">{t("dashboard.cashIn")}</CardTitle>
                </div>
                <p className="text-xs sm:text-lg font-bold whitespace-nowrap text-right" style={{ color: "var(--income)" }}>
                  {formatAmount(totalIn)}
                </p>
              </div>
            </CardHeader>
            <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
              {incomeCats && incomeCats.length > 0 ? (
                <div className="space-y-1 sm:space-y-2">
                  {incomeCats.map((cat) => (
                    <div key={cat.category} className="flex items-center justify-between py-1.5 sm:py-2 px-2 sm:px-3 rounded-lg hover:bg-muted/50 gap-2">
                      <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                        <div className="h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full flex-shrink-0" style={{ backgroundColor: "var(--income)" }} />
                        <span className="text-xs sm:text-sm text-foreground truncate">
                          {getCategoryLabel(cat.category ?? "uncategorized")}
                        </span>
                        <span className="text-[10px] sm:text-xs text-muted-foreground flex-shrink-0">({cat.count})</span>
                      </div>
                      <span className="text-xs sm:text-sm font-medium tabular-nums whitespace-nowrap flex-shrink-0" style={{ color: "var(--income)" }}>
                        +{formatAmount(cat.total ?? "0")}
                      </span>
                    </div>
                  ))}
                  <Separator className="my-1.5 sm:my-2" />
                  <div className="flex items-center justify-between py-1.5 sm:py-2 px-2 sm:px-3 rounded-lg gap-2" style={{ backgroundColor: "oklch(0.78 0.15 85 / 0.1)" }}>
                    <span className="text-xs sm:text-sm font-semibold" style={{ color: "var(--total)" }}>{language === "ar" ? "إجمالي الداخل" : "Total Cash In"}</span>
                    <span className="text-xs sm:text-sm font-bold tabular-nums whitespace-nowrap" style={{ color: "var(--total)" }}>
                      {formatAmount(totalIn)}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4 sm:py-6 text-muted-foreground">
                  <ArrowDownLeft className="h-5 w-5 sm:h-6 sm:w-6 mx-auto mb-2 opacity-50" />
                  <p className="text-xs sm:text-sm">No income recorded this period</p>
                  <p className="text-[10px] sm:text-xs mt-1">Parse bank SMS with credits to see income here</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Cash Out Section -- red accent border */}
          <Card className="border-l-4" style={{ borderLeftColor: "var(--expense)" }}>
            <CardHeader className="pb-2 sm:pb-3 px-3 sm:px-6 pt-3 sm:pt-6">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <div className="h-6 w-6 rounded-md flex items-center justify-center" style={{ backgroundColor: "oklch(0.58 0.22 25 / 0.12)" }}>
                    <ArrowUpRight className="h-3.5 w-3.5" style={{ color: "var(--expense)" }} />
                  </div>
                  <CardTitle className="text-sm sm:text-base">{t("dashboard.cashOut")}</CardTitle>
                </div>
                <p className="text-xs sm:text-lg font-bold whitespace-nowrap text-right" style={{ color: "var(--expense)" }}>
                  {formatAmount(totalOut)}
                </p>
              </div>
            </CardHeader>
            <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
              {expenseCats && expenseCats.length > 0 ? (
                <div className="space-y-1 sm:space-y-2">
                  {expenseCats.map((cat) => (
                    <div key={cat.category} className="flex items-center justify-between py-1.5 sm:py-2 px-2 sm:px-3 rounded-lg hover:bg-muted/50 gap-2">
                      <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                        <div className="h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full flex-shrink-0" style={{ backgroundColor: "var(--expense)" }} />
                        <span className="text-xs sm:text-sm text-foreground truncate">
                          {getCategoryLabel(cat.category ?? "uncategorized")}
                        </span>
                        <span className="text-[10px] sm:text-xs text-muted-foreground flex-shrink-0">({cat.count})</span>
                      </div>
                      <span className="text-xs sm:text-sm font-medium tabular-nums whitespace-nowrap flex-shrink-0" style={{ color: "var(--expense)" }}>
                        -{formatAmount(cat.total ?? "0")}
                      </span>
                    </div>
                  ))}
                  <Separator className="my-1.5 sm:my-2" />
                  <div className="flex items-center justify-between py-1.5 sm:py-2 px-2 sm:px-3 rounded-lg gap-2" style={{ backgroundColor: "oklch(0.78 0.15 85 / 0.1)" }}>
                    <span className="text-xs sm:text-sm font-semibold" style={{ color: "var(--total)" }}>{language === "ar" ? "إجمالي الخارج" : "Total Cash Out"}</span>
                    <span className="text-xs sm:text-sm font-bold tabular-nums whitespace-nowrap" style={{ color: "var(--total)" }}>
                      {formatAmount(totalOut)}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4 sm:py-6 text-muted-foreground">
                  <ArrowUpRight className="h-5 w-5 sm:h-6 sm:w-6 mx-auto mb-2 opacity-50" />
                  <p className="text-xs sm:text-sm">No expenses recorded this period</p>
                  <p className="text-[10px] sm:text-xs mt-1">Parse bank SMS with debits to see expenses here</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Adjustment Disclosure */}
          {adjustmentStats.count > 0 && (
            <div className="rounded-xl border border-dashed border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-950/20 p-3 sm:p-4">
              <div className="flex items-start gap-2.5">
                <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "oklch(0.75 0.15 85 / 0.15)" }}>
                  <AlertTriangle className="h-3.5 w-3.5 sm:h-4 sm:w-4" style={{ color: "oklch(0.75 0.15 85)" }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-amber-800 dark:text-amber-300">
                    {language === "ar" ? "تسويات الرصيد" : "Balance Adjustments"}
                  </p>
                  <p className="text-[10px] sm:text-xs text-amber-700/80 dark:text-amber-400/70 mt-0.5">
                    {language === "ar"
                      ? `${adjustmentStats.count} تسوية بصافي ${adjustmentStats.netTotal >= 0 ? "+" : ""}${formatAmount(adjustmentStats.netTotal)} — أرقام تقريبية لسد الفجوة بين الرصيد المحسوب ورصيد البنك الفعلي`
                      : `${adjustmentStats.count} adjustment${adjustmentStats.count !== 1 ? "s" : ""} with net ${adjustmentStats.netTotal >= 0 ? "+" : ""}${formatAmount(adjustmentStats.netTotal)} — plug figures to close the gap between calculated and reported bank balance`}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Net Cash Flow -- dashed border like Budgets unbudgeted */}
          <div
            className="rounded-xl border-2 border-dashed p-4 sm:p-5 cursor-pointer hover:ring-2 hover:ring-primary/30 transition-all"
            style={{ borderColor: netFlow >= 0 ? "var(--income)" : "var(--expense)", backgroundColor: netFlow >= 0 ? "oklch(0.65 0.18 155 / 0.05)" : "oklch(0.58 0.22 25 / 0.05)" }}
            onClick={() => setLocation("/analytics")}
          >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-start gap-2 sm:gap-3 min-w-0">
                  {netFlow >= 0 ? (
                    <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0 mt-0.5" style={{ color: "var(--income)" }} />
                  ) : (
                    <TrendingDown className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0 mt-0.5" style={{ color: "var(--expense)" }} />
                  )}
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-muted-foreground">{t("income.netCashFlow")}</p>
                    <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">
                      {netFlow >= 0 ? (language === "ar" ? "فائض" : "Surplus") : (language === "ar" ? "عجز" : "Deficit")}
                    </p>
                  </div>
                </div>
                <p
                  className="text-sm sm:text-2xl font-bold tabular-nums whitespace-nowrap text-right"
                  style={{ color: netFlow >= 0 ? "var(--income)" : "var(--expense)" }}
                >
                  {netFlow >= 0 ? "+" : ""}{formatAmount(netFlow)}
                </p>
              </div>
          </div>

          {/* ─── Reconciliation Line — shown in EVERY view ─────────────────── */}
          {(startingBalance != null || currentBalance != null) && (() => {
            const opening = startingBalance ?? 0;
            const closing = currentBalance ?? 0;
            const expectedClosing = opening + netFlow;
            const difference = closing - expectedClosing;
            const hasDifference = Math.abs(difference) > 0.001;
            return (
              <Card className={`border ${hasDifference ? 'border-amber-300 dark:border-amber-700 bg-amber-50/30 dark:bg-amber-950/20' : 'border-border'}`}>
                <CardHeader className="pb-2 sm:pb-3 px-3 sm:px-6 pt-3 sm:pt-6">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <Scale className={`h-4 w-4 sm:h-5 sm:w-5 ${hasDifference ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`} />
                    <CardTitle className="text-sm sm:text-base">
                      {language === "ar" ? "تسوية الرصيد" : "Balance Reconciliation"}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
                  <div className="space-y-2">
                    {/* Opening + Net = Expected Closing */}
                    <div className="flex items-center justify-between py-1.5 px-2 sm:px-3 rounded-lg hover:bg-muted/30 gap-2">
                      <span className="text-xs sm:text-sm text-muted-foreground">
                        {language === "ar" ? "رصيد بداية الشهر" : "Opening Balance"}
                      </span>
                      <span className="text-xs sm:text-sm font-medium tabular-nums">{startingBalance != null ? formatAmount(opening) : "—"}</span>
                    </div>
                    <div className="flex items-center justify-between py-1.5 px-2 sm:px-3 rounded-lg hover:bg-muted/30 gap-2">
                      <span className="text-xs sm:text-sm text-muted-foreground">
                        {language === "ar" ? "صافي التدفق النقدي" : "Net Cash Flow"}
                      </span>
                      <span className={`text-xs sm:text-sm font-medium tabular-nums ${netFlow >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {netFlow >= 0 ? "+" : ""}{formatAmount(netFlow)}
                      </span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between py-1.5 px-2 sm:px-3 rounded-lg hover:bg-muted/30 gap-2">
                      <span className="text-xs sm:text-sm font-medium text-foreground">
                        {language === "ar" ? "الرصيد المتوقع" : "Expected Closing"}
                      </span>
                      <span className="text-xs sm:text-sm font-bold tabular-nums">{formatAmount(expectedClosing)}</span>
                    </div>
                    <div className="flex items-center justify-between py-1.5 px-2 sm:px-3 rounded-lg hover:bg-muted/30 gap-2">
                      <span className="text-xs sm:text-sm font-medium text-foreground">
                        {language === "ar" ? "الرصيد الفعلي" : "Actual Closing"}
                      </span>
                      <span className="text-xs sm:text-sm font-bold tabular-nums">{currentBalance != null ? formatAmount(closing) : "—"}</span>
                    </div>
                    {hasDifference && (
                      <>
                        <Separator />
                        <div className="flex items-center justify-between py-2 px-2 sm:px-3 rounded-lg bg-amber-100/50 dark:bg-amber-900/20 gap-2">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <AlertTriangle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                            <span className="text-xs sm:text-sm font-semibold text-amber-800 dark:text-amber-300">
                              {language === "ar" ? "فرق غير مفسر" : "Unexplained Difference"}
                            </span>
                          </div>
                          <span className="text-xs sm:text-sm font-bold tabular-nums text-amber-800 dark:text-amber-300">
                            {difference > 0 ? "+" : ""}{formatAmount(difference)}
                          </span>
                        </div>
                        <p className="text-[10px] sm:text-xs text-amber-700/80 dark:text-amber-400/70 px-2 sm:px-3">
                          {language === "ar"
                            ? "الرصيد الفعلي لا يتطابق مع الرصيد المحسوب. قد يعني ذلك وجود معاملات مفقودة أو أن كشوفات من حسابات مختلفة تم دمجها."
                            : "Actual closing balance does not match the calculated balance. This may indicate missing transactions or statements from different accounts merged together."}
                        </p>
                        <Link href="/accounts" className="flex items-center gap-1 text-[10px] sm:text-xs text-primary hover:underline px-2 sm:px-3 w-fit">
                          <ExternalLink className="h-3 w-3" />
                          {language === "ar" ? "مراجعة الحسابات" : "Review accounts"}
                        </Link>
                      </>
                    )}
                    {!hasDifference && (
                      <div className="flex items-center gap-1.5 py-1.5 px-2 sm:px-3">
                        <div className="h-2 w-2 rounded-full bg-green-500" />
                        <span className="text-[10px] sm:text-xs text-green-700 dark:text-green-400">
                          {language === "ar" ? "الرصيد متوازن — لا يوجد فرق" : "Balance ties — no unexplained difference"}
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })()}
      </div>
    </div>
  );
}
